import React, { FC, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { AxiosError } from "axios";
import { useSnackbar } from "notistack";
import ReactConfetti from "react-confetti";
import {
  FormBaseType,
  FormBasicsDto,
  FormSectionDto,
} from "@green0meter/api-client";
import { FieldValues } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useCreateSubmission, useGetAvailableForms } from "@api";
import { MultistepForm, WelcomeBanner } from "@components/common";
import { Box, CircularProgress } from "@components/base";
import { useUserStore } from "@store/user";

const BrsrFormPage: FC = () => {
  const { formId } = useParams();
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formBasics, setFormBasics] = useState<FormBasicsDto>();

  const { t } = useTranslation();

  const { user } = useUserStore();
  const {
    createSubmissionAsync,
    createSubmissionEnabled,
    isPendingCreateSubmission,
    createSubmissionError,
  } = useCreateSubmission(user);
  const { availableForms, isFetchingAvailableForms } = useGetAvailableForms(user, [
    FormBaseType.BRSR,
  ]);
  const { enqueueSnackbar } = useSnackbar();

  const handleSubmit = async (
    values: FieldValues,
    sections: FormSectionDto[],
  ): Promise<void> => {
    const isValid = sections.every((section) =>
      section.formQuestions?.every((question) => {
        if (question.isRequired) {
          return (
            values[question.questionId] !== undefined &&
            values[question.questionId] !== ""
          );
        }

        return true;
      }),
    );

    if (formId && isValid && createSubmissionEnabled) {
      await createSubmissionAsync({
        formId,
      });
      setFormSubmitted(true);
    }
  };

  useEffect(() => {
    const errorData = (createSubmissionError as AxiosError)?.response?.data as {
      message: string;
    };

    if (errorData) {
      enqueueSnackbar(errorData.message, {
        variant: "error",
      });
    }
  }, [createSubmissionError]);

  useEffect(() => {
    setFormBasics(availableForms?.find((form) => form.id === formId));
  }, [availableForms]);

  return formSubmitted ? (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      minHeight="50vh"
      gap={2}
    >
      <ReactConfetti
        width={window.innerWidth}
        height={window.innerHeight}
        recycle={false}
        numberOfPieces={200}
      />
      <WelcomeBanner
        title={t("formSubmitted.title")}
        text={t("formSubmitted.text")}
        color="green"
        icon="accountant"
      />
    </Box>
  ) : isFetchingAvailableForms ? (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
      <CircularProgress />
    </Box>
  ) : formBasics ? (
    <MultistepForm
      formSettings={{
        formBasics,
        returnUrl: "/brsr",
        button: createSubmissionEnabled
          ? {
              label: t("form.submit", "Submit"),
              onClick: (): void => {},
              popoverButtons: [
                {
                  title: t("form.submitCalculate.title"),
                  description: t("form.submitCalculate.description"),
                  onClick: handleSubmit,
                },
                {
                  title: t("form.submitCreateReports.title"),
                  description: t("form.submitCreateReports.description"),
                  onClick: handleSubmit,
                },
              ],
            }
          : undefined,
      }}
      isSubmissionPending={isPendingCreateSubmission}
    />
  ) : (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
      <WelcomeBanner
        title={t("formNotFound.title")}
        text={t("formNotFound.text")}
        color="red"
        icon="accountant"
      />
    </Box>
  );
};

export default BrsrFormPage;
